<script setup>
import { computed } from 'vue';
import ContactSlide from '@components/offer-card/ContactSlide.vue';
import Labels from '@components/offer-card/Labels.vue';
import MediaImage from '@components/MediaImage.vue';
import Carousel from '@components/offer-card/Carousel.vue';
import HitType from '@components/offer-card/HitType.vue';
import StreetDistrict from '@components/offer-card/StreetDistrict.vue';
import OfferPrice from '@components/offer-card/OfferPrice.vue';
import { trans } from '@elements/translation';

const props = defineProps({
  firstOffer: {
    type: Object,
    required: true,
  },
  offers: {
    type: Array,
    default: () => [],
  },
  isSingleOpenspace: {
    type: Boolean,
    default: false,
  },
  url: {
    type: String,
    default: null,
  },
  proposal: {
    type: Object,
    default: null,
  },
  imageRatio: {
    type: Array,
    default: () => [ 290, 230 ],
  },
  width: {
    type: Number,
    default: 290,
  },
  carouselHeight: {
    type: Number,
    default: 230,
  },
  isOwnerView: {
    type: Boolean,
    default: false,
  },
  isContacted: {
    type: Boolean,
    default: false,
  },
  target: {
    type: String,
    default: '_blank',
  },
});

const cdnUrl = computed(() => window.CDN_URL);

const firstStationDistance = computed(() => props.firstOffer.first_station_distance);

const promoteTypeCode = computed(() => props.firstOffer.promote_types?.[0]?.code);

const hasCarousel = computed(() => !props.ownerView);

const sliderImages = computed(() => props.firstOffer.slider_images);

const offerUrl = computed(() => {
  if (props.url === null && !props.offers.length) {
    return props.firstOffer.public_url;
  }

  return props.url;
});

const clearImages = computed(() => {
  if (hasCarousel.value) {
    return sliderImages.value.slice(0, 12);
  }
  return [ sliderImages.value[0] ] ?? [];
});

const hasMoreMedia = computed(() => hasCarousel.value && sliderImages.value.length > 12);

const contactImage = computed(() => {
  if (hasMoreMedia.value) {
    return sliderImages.value[12];
  }
  return sliderImages.value[sliderImages.value.length - 1] ?? null;
});

const contactText = computed(() => trans(hasMoreMedia.value ? 'offer_card.contact_slide.more_pictures' : 'offer_card.contact_slide.know_more'));

const displayedInfos = computed(() => (props.offers.length <= 1 ? props.firstOffer.offer_infos : props.proposal.offers_infos));

const displayedPrice = computed(() => {
  if (props.offers.length <= 1) {
    return props.firstOffer.price;
  }

  return props.proposal.total_price;
});

const openOffer = ((event) => {
  if (props.url === null && props.offers.length) {
    event.preventDefault();
  }
});
</script>

<template>
  <article class="real-estate-card offer-card">
    <a
      class="offer-card__link"
      :href="offerUrl"
      :target="target"
      :title="firstOffer.name"
      @click="openOffer($event)"
    >
      <div class="offer-card__slider offer-card__slider--flickity">
        <Labels
          :offer="firstOffer"
          :proposal="proposal"
          :is-owner-view="isOwnerView"
          :is-contacted="isContacted"
        />

        <div class="offer-card__actions">
          <slot name="actions" />
        </div>

        <Carousel :height="carouselHeight">
          <MediaImage
            v-for="uuid in clearImages"
            :key="uuid"
            :ratio="imageRatio"
            :width="290"
            :uuid="uuid"
            :alt="firstOffer.name"
            is-lazy-flickity
          />
          <ContactSlide
            v-if="hasCarousel && contactImage"
            :uuid="contactImage"
            :text="contactText"
            :link="url ?? firstOffer.public_url"
          />
        </Carousel>
      </div>

      <div class="offer-card__infos">
        <div class="offer-card__container">
          <span
            v-if="promoteTypeCode"
            :class="`offer-card__container__promote-type offer-card__container__promote-type--${promoteTypeCode}`"
          >
            {{ trans(`offer.promote_type.${promoteTypeCode}`) }}
          </span>

          <span v-if="isOwnerView" class="offer-card__offer-title">{{ firstOffer.name }}</span>

          <div class="offer-card__container__link">
            <StreetDistrict v-if="firstOffer.address !== null" :address="firstOffer.address" />

            <ul
              v-if="firstStationDistance && !isOwnerView"
              class="offer-card__transport"
            >
              <li
                v-for="unique_line in firstStationDistance.unique_lines"
                :key="unique_line"
                class="offer-card__transport__image"
              >
                <img
                  :src="`${cdnUrl}/images/img_trans.gif`"
                  :class="`icon-${unique_line}`"
                  :alt="unique_line"
                >
              </li>
            </ul>
          </div>

          <HitType
            :first-offer="firstOffer"
            :offers="offers"
            :is-owner-view="isOwnerView"
          />

          <div class="offer-card__container-infos">
            <div class="offer-card__container-infos__text">
              {{ displayedInfos }}
            </div>
          </div>

          <div v-if="!!$slots.services" class="offer-card__container__services">
            <slot name="services" />
          </div>

          <OfferPrice
            class="offer-card__container__price"
            :offer="firstOffer"
            :price="displayedPrice"
            :is-single-openspace="isSingleOpenspace"
          />

          <div class="offer-card__container__body-footer">
            <slot name="body-footer" />
          </div>
        </div>
      </div>

      <div class="offer-card__footer">
        <slot name="footer" />
      </div>
    </a>
  </article>
</template>
